import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { workflowsApi } from './api'
import type { WorkflowPayload } from './types'

export function useWorkflows() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'workflows'),
    queryFn: workflowsApi.list,
  })
}

type MutationKind = 'run' | 'pause' | 'resume' | 'archive' | 'remove'

export function useWorkflowAction() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: MutationKind }) => {
      switch (kind) {
        case 'run': return workflowsApi.run(id)
        case 'pause': return workflowsApi.pause(id)
        case 'resume': return workflowsApi.resume(id)
        case 'archive': return workflowsApi.archive(id)
        case 'remove': return workflowsApi.remove(id)
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'workflows') }),
  })
}

export function useCreateWorkflow() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: WorkflowPayload) => workflowsApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'workflows') }),
  })
}

export function useUpdateWorkflow() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: WorkflowPayload }) =>
      workflowsApi.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'workflows') }),
  })
}

export function useWorkflowRuns(id: string | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'workflows', id ?? 'none', 'runs'),
    queryFn: () => workflowsApi.runs(id!, 50),
    enabled: !!id,
  })
}

export function useAllWorkflowRuns(enabled: boolean) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'workflows', 'all-runs'),
    queryFn: () => workflowsApi.allRuns(100),
    enabled,
  })
}
